import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Clock, Save, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const BusinessHoursManager = () => {
  const [businessHours, setBusinessHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  // Format time helper function (must be defined before generateTimeSlots)
  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Generate time slots from 6 AM to 10 PM in 30-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 22; hour++) {
      for (let minute of [0, 30]) {
        if (hour === 22 && minute === 30) break; // Stop at 10:00 PM
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = formatTime(time);
        slots.push({ value: time, label: displayTime });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  useEffect(() => {
    loadBusinessHours();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBusinessHours = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/business-hours`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log('[BusinessHoursManager] API response:', response.data);

      // Ensure all days are present
      const hoursMap = {};
      if (Array.isArray(response.data)) {
        response.data.forEach(hour => {
          // Normalize time format from HH:MM:SS to HH:MM (handle null/undefined)
          const startTime = hour.start_time ? hour.start_time.substring(0, 5) : '09:00';
          const endTime = hour.end_time ? hour.end_time.substring(0, 5) : '17:00';
          const normalizedHour = {
            ...hour,
            start_time: startTime,
            end_time: endTime,
            is_active: hour.is_active !== undefined ? hour.is_active : true,
          };
          hoursMap[hour.day_of_week] = normalizedHour;
        });
      }

      // Fill in missing days with defaults
      const allDays = daysOfWeek.map(day => {
        if (hoursMap[day.value]) {
          return hoursMap[day.value];
        }
        return {
          day_of_week: day.value,
          start_time: '09:00',
          end_time: '17:00',
          is_active: true, // All days open by default (admin can close any day)
        };
      });

      console.log('[BusinessHoursManager] Setting business hours:', allDays);
      setBusinessHours(allDays);
    } catch (error) {
      console.error('Load business hours error:', error);
      // Still set default hours so the component renders
      const defaultHours = daysOfWeek.map(day => ({
        day_of_week: day.value,
        start_time: '09:00',
        end_time: '17:00',
        is_active: true,
      }));
      setBusinessHours(defaultHours);
      toast.error('Failed to load business hours - using defaults');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDay = (dayOfWeek) => {
    setBusinessHours(prev =>
      prev.map(hour =>
        hour.day_of_week === dayOfWeek
          ? { ...hour, is_active: !hour.is_active }
          : hour
      )
    );
  };

  const handleTimeChange = (dayOfWeek, field, value) => {
    setBusinessHours(prev =>
      prev.map(hour =>
        hour.day_of_week === dayOfWeek
          ? { ...hour, [field]: value }
          : hour
      )
    );
  };

  const handleSave = async () => {
    // Validate that end time is after start time
    for (const hour of businessHours) {
      if (hour.is_active) {
        const start = hour.start_time.split(':').map(Number);
        const end = hour.end_time.split(':').map(Number);
        const startMinutes = start[0] * 60 + start[1];
        const endMinutes = end[0] * 60 + end[1];

        if (endMinutes <= startMinutes) {
          const dayName = daysOfWeek.find(d => d.value === hour.day_of_week)?.label;
          toast.error(`${dayName}: End time must be after start time`);
          return;
        }
      }
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_URL}/business-hours`,
        { hours: businessHours },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Business hours saved successfully!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.error || 'Failed to save business hours');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToAll = (sourceDay) => {
    const source = businessHours.find(h => h.day_of_week === sourceDay);
    if (!source) return;

    if (window.confirm('Copy these hours to all other days?')) {
      setBusinessHours(prev =>
        prev.map(hour => ({
          ...hour,
          start_time: source.start_time,
          end_time: source.end_time,
          is_active: source.is_active,
        }))
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <Clock className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Business Operating Hours</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Set the actual hours your business is open. These hours will constrain when employees can set their schedules 
        and when clients can book appointments.
      </p>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
        <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
        <div className="text-sm text-blue-800">
          <strong>Important:</strong> Employee schedules cannot exceed these business hours. 
          If you reduce business hours, employee schedules will be automatically adjusted.
        </div>
      </div>

      <div className="space-y-3">
        {businessHours.map((hour, index) => {
          const dayInfo = daysOfWeek.find(d => d.value === hour.day_of_week);
          
          return (
            <div
              key={hour.day_of_week}
              className={`p-4 rounded-lg border-2 transition-all ${
                hour.is_active
                  ? 'bg-white border-gray-200'
                  : 'bg-white border-gray-300 border-dashed'
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Day Toggle */}
                <div className="flex items-center space-x-3 md:w-40">
                  <input
                    type="checkbox"
                    id={`day-${hour.day_of_week}`}
                    checked={hour.is_active}
                    onChange={() => handleToggleDay(hour.day_of_week)}
                    className="w-5 h-5 text-primary-600 rounded cursor-pointer focus:ring-2 focus:ring-primary-500"
                  />
                  <label
                    htmlFor={`day-${hour.day_of_week}`}
                    className={`font-semibold cursor-pointer ${
                      hour.is_active ? 'text-gray-900' : 'text-gray-500'
                    }`}
                  >
                    {dayInfo?.label}
                  </label>
                </div>

                {/* Time Selectors */}
                {hour.is_active ? (
                  <>
                    <div className="flex items-center space-x-2 flex-1">
                      <select
                        value={hour.start_time}
                        onChange={(e) => handleTimeChange(hour.day_of_week, 'start_time', e.target.value)}
                        className="input text-sm"
                      >
                        {timeSlots.map(slot => (
                          <option key={slot.value} value={slot.value}>
                            {slot.label}
                          </option>
                        ))}
                      </select>
                      <span className="text-gray-500 font-medium">to</span>
                      <select
                        value={hour.end_time}
                        onChange={(e) => handleTimeChange(hour.day_of_week, 'end_time', e.target.value)}
                        className="input text-sm"
                      >
                        {timeSlots.map(slot => (
                          <option key={slot.value} value={slot.value}>
                            {slot.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Copy Button */}
                    <button
                      onClick={() => handleCopyToAll(hour.day_of_week)}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                    >
                      Copy to All
                    </button>
                  </>
                ) : (
                  <div className="text-gray-400 italic font-medium flex-1">
                    Closed (click checkbox to enable)
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-semibold text-gray-700 mb-3">Quick Actions</h3>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              console.log('Monday-Friday clicked');
              setBusinessHours(prev =>
                prev.map(h => ({ ...h, is_active: h.day_of_week >= 1 && h.day_of_week <= 5 }))
              );
            }}
            className="btn btn-secondary text-sm"
          >
            Monday - Friday Only
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              console.log('Monday-Saturday clicked');
              setBusinessHours(prev =>
                prev.map(h => ({ ...h, is_active: h.day_of_week >= 1 && h.day_of_week <= 6 }))
              );
            }}
            className="btn btn-secondary text-sm"
          >
            Monday - Saturday
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              console.log('Open All Days clicked');
              setBusinessHours(prev =>
                prev.map(h => ({ ...h, is_active: true }))
              );
            }}
            className="btn btn-secondary text-sm"
          >
            Open All Days
          </button>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Save size={18} />
          <span>{saving ? 'Saving...' : 'Save Business Hours'}</span>
        </button>
      </div>
    </div>
  );
};

export default BusinessHoursManager;
